import React, { useState, useRef, useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import { RegularText, MediumText, DemiLightText } from '@/lib/Text';
import { Row } from '@/lib/utils';
import SvgIcon from '@/components/SvgIcon';
import { colors } from '@/lib/colors';

dayjs.locale('ko');

interface Props {
    onClose: () => void;
    onConfirm: (dateStr: string) => void;
    initialDate?: string | null;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 10, 20, 30, 40, 50];
const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

const SchedulePicker = ({ onClose, onConfirm, initialDate }: Props) => {
    const init = initialDate ? dayjs(initialDate) : dayjs();
    const [selectedDate, setSelectedDate] = useState<Dayjs>(init);
    const [selectedHour, setSelectedHour] = useState(init.hour());
    const [selectedMinute, setSelectedMinute] = useState(Math.floor(init.minute() / 10) * 10);
    const [openPanel, setOpenPanel] = useState<'none' | 'calendar' | 'hour' | 'minute'>('none');
    const [viewMonth, setViewMonth] = useState(init.startOf('month'));

    const containerRef = useRef<HTMLDivElement>(null);
    const hourListRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (openPanel === 'hour' && hourListRef.current) {
            const selectedEl = hourListRef.current.querySelector('[data-selected="true"]');
            if (selectedEl) {
                selectedEl.scrollIntoView({ block: 'center' });
            }
        }
    }, [openPanel]);

    // Outside click to close
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const handleConfirm = () => {
        const result = selectedDate.hour(selectedHour).minute(selectedMinute).second(0).millisecond(0);
        onConfirm(result.toISOString());
    };

    const togglePanel = (panel: 'calendar' | 'hour' | 'minute') => {
        setOpenPanel(openPanel === panel ? 'none' : panel);
    };

    const [hoveredCell, setHoveredCell] = useState<string | null>(null);

    const renderCalendar = () => {
        const startOfMonth = viewMonth.startOf('month');
        const daysInMonth = viewMonth.daysInMonth();
        const startDayOfWeek = startOfMonth.day();

        const prevMonth = viewMonth.subtract(1, 'month');
        const daysInPrevMonth = prevMonth.daysInMonth();

        const cells: { date: Dayjs; isCurrentMonth: boolean }[] = [];

        for (let i = startDayOfWeek - 1; i >= 0; i--) {
            cells.push({ date: prevMonth.date(daysInPrevMonth - i), isCurrentMonth: false });
        }
        for (let d = 1; d <= daysInMonth; d++) {
            cells.push({ date: viewMonth.date(d), isCurrentMonth: true });
        }
        const nextMonth = viewMonth.add(1, 'month');
        const remaining = (7 - (cells.length % 7)) % 7;
        for (let d = 1; d <= remaining; d++) {
            cells.push({ date: nextMonth.date(d), isCurrentMonth: false });
        }

        const weeks: (typeof cells)[] = [];
        for (let i = 0; i < cells.length; i += 7) {
            weeks.push(cells.slice(i, i + 7));
        }

        const today = dayjs();

        return (
            <div className="pb-[8px]">
                {/* Month navigation */}
                <Row className="items-center justify-center gap-[20px] py-[14px]">
                    <div
                        className="cursor-pointer p-[4px]"
                        onClick={() => setViewMonth(viewMonth.subtract(1, 'month'))}
                    >
                        <SvgIcon
                            name="SvgLeftStrokeArrow"
                            size={12}
                            fill={colors.trans}
                            stroke="#191F28"
                            strokeWidth={1.5}
                        />
                    </div>
                    <MediumText fontSize={16} lineHeight={24} color="#191F28">
                        {viewMonth.format('YYYY.MM')}
                    </MediumText>
                    <div className="cursor-pointer p-[4px]" onClick={() => setViewMonth(viewMonth.add(1, 'month'))}>
                        <SvgIcon
                            name="SvgRightStrokeArrow"
                            size={12}
                            fill={colors.trans}
                            stroke="#191F28"
                            strokeWidth={1.5}
                        />
                    </div>
                </Row>

                {/* Day headers */}
                <div className="grid grid-cols-7 mb-[2px]">
                    {DAY_LABELS.map((label, i) => (
                        <div key={label} className="flex items-center justify-center h-[30px]">
                            <DemiLightText fontSize={13} lineHeight={20} color={i === 0 ? '#FF4D4F' : '#8B9194'}>
                                {label}
                            </DemiLightText>
                        </div>
                    ))}
                </div>

                {/* Date cells */}
                {weeks.map((week, wi) => (
                    <div key={wi} className="grid grid-cols-7">
                        {week.map((cell, di) => {
                            const cellKey = `${wi}-${di}`;
                            const isToday = cell.date.isSame(today, 'day');
                            const isSelected = cell.date.isSame(selectedDate, 'day') && cell.isCurrentMonth;
                            const isHovered = hoveredCell === cellKey && !isSelected;
                            const isSunday = cell.date.day() === 0;

                            const bgClass = isSelected
                                ? 'bg-[#E8EDF2]'
                                : isHovered
                                  ? 'bg-[#E8EDF2]'
                                  : '';

                            const borderClass = isToday && !isSelected && !isHovered
                                ? 'border border-solid border-primary'
                                : '';

                            const textColor = isSelected || isHovered
                                ? '#3182F6'
                                : !cell.isCurrentMonth
                                  ? '#D1D5D9'
                                  : isSunday
                                    ? '#FF4D4F'
                                    : '#191F28';

                            return (
                                <div
                                    key={di}
                                    className="flex items-center justify-center h-[36px] cursor-pointer"
                                    onMouseEnter={() => setHoveredCell(cellKey)}
                                    onMouseLeave={() => setHoveredCell(null)}
                                    onClick={() => {
                                        setSelectedDate(cell.date);
                                        if (!cell.isCurrentMonth) {
                                            setViewMonth(cell.date.startOf('month'));
                                        }
                                        setOpenPanel('none');
                                    }}
                                >
                                    <div
                                        className={`w-[32px] h-[32px] flex items-center justify-center rounded-full ${bgClass} ${borderClass}`}
                                    >
                                        <RegularText
                                            fontSize={14}
                                            lineHeight={20}
                                            color={textColor}
                                        >
                                            {cell.date.date()}
                                        </RegularText>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    };

    const formattedDate = selectedDate.format('YYYY. MM. DD (dd)');

    return (
        <div
            ref={containerRef}
            className="absolute top-[42px] right-0 w-[340px] bg-white border border-solid border-[#E3E6E9] rounded-[16px] p-[20px] z-30 shadow-[0px_4px_20px_0px_rgba(0,0,0,0.08)]"
        >
            {/* Title */}
            <div className="mb-[9px]">
                <DemiLightText fontSize={14} lineHeight={20} color={colors.Text_1}>
                    예약 시간
                </DemiLightText>
            </div>

            {/* Date/Time selectors */}
            <Row className="gap-[8px] mb-[20px] relative">
                {/* Date button */}
                <div className="relative">
                    <div
                        className={`px-[14px] py-[8px] border border-solid rounded-[6px] cursor-pointer transition-colors hover:border-primary ${
                            openPanel === 'calendar' ? 'border-primary' : 'border-[#E6E8EA]'
                        }`}
                        onClick={() => togglePanel('calendar')}
                    >
                        <RegularText fontSize={14} lineHeight={20} color="#191F28" letterSpacing={-0.4}>
                            {formattedDate}
                        </RegularText>
                    </div>

                    {/* Calendar dropdown */}
                    {openPanel === 'calendar' && (
                        <div className="absolute top-[42px] left-0 w-[300px] bg-white border border-solid border-[#E6E8EA] rounded-[12px] py-[4px] z-20 shadow-[0px_4px_12px_0px_rgba(0,0,0,0.08)]">
                            {renderCalendar()}
                        </div>
                    )}
                </div>

                {/* Hour button */}
                <div className="relative">
                    <div
                        className={`px-[12px] py-[8px] border border-solid rounded-[6px] cursor-pointer flex items-center gap-[12px] transition-colors hover:border-primary ${
                            openPanel === 'hour' ? 'border-primary' : 'border-[#E6E8EA]'
                        }`}
                        onClick={() => togglePanel('hour')}
                    >
                        <RegularText fontSize={14} lineHeight={20} color="#191F28" letterSpacing={-0.4}>
                            {selectedHour}시
                        </RegularText>
                        <SvgIcon
                            name="SvgDownStrokeArrow"
                            size={10}
                            fill={colors.trans}
                            stroke={colors.Text_1}
                            strokeWidth={1.5}
                            className={`transition-transform ${openPanel === 'hour' ? 'rotate-180' : ''}`}
                        />
                    </div>

                    {/* Hour dropdown */}
                    {openPanel === 'hour' && (
                        <div
                            ref={hourListRef}
                            className="flex flex-col absolute top-[42px] left-0 w-[100px] max-h-[240px] overflow-y-auto bg-white border border-solid border-[#E6E8EA] rounded-[15px]  z-20 shadow-[0px_4px_12px_0px_rgba(0,0,0,0.08)] gap-y-2 py-2"
                        >
                            {HOURS.map((h) => (
                                <div
                                    key={h}
                                    data-selected={h === selectedHour}
                                    className={`px-[16px] mx-3 py-[8px] cursor-pointer rounded-[7px] h-[33px] ${
                                        h === selectedHour ? 'bg-[#EBF4FF]' : 'hover:bg-[#F6F8FA]'
                                    }`}
                                    onClick={() => {
                                        setSelectedHour(h);
                                        setOpenPanel('none');
                                    }}
                                >
                                    <RegularText
                                        fontSize={15}
                                        lineHeight={15}
                                        color={h === selectedHour ? '#3182F6' : '#191F28'}
                                    >
                                        {String(h).padStart(2, '0')}시
                                    </RegularText>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Minute button */}
                <div className="relative">
                    <div
                        className={`px-[12px] py-[8px] border border-solid rounded-[6px] cursor-pointer flex items-center gap-[12px] transition-colors hover:border-primary ${
                            openPanel === 'minute' ? 'border-primary' : 'border-[#E6E8EA]'
                        }`}
                        onClick={() => togglePanel('minute')}
                    >
                        <RegularText fontSize={14} lineHeight={20} color="#191F28" letterSpacing={-0.4}>
                            {String(selectedMinute).padStart(2, '0')}분
                        </RegularText>
                        <SvgIcon
                            name="SvgDownStrokeArrow"
                            size={10}
                            fill={colors.trans}
                            stroke={colors.Text_1}
                            strokeWidth={1.5}
                            className={`transition-transform ${openPanel === 'minute' ? 'rotate-180' : ''}`}
                        />
                    </div>

                    {/* Minute dropdown */}
                    {openPanel === 'minute' && (
                        <div className="flex flex-col absolute top-[42px] left-0 w-[100px] max-h-[240px] overflow-y-auto bg-white border border-solid border-[#E6E8EA] rounded-[15px]  z-20 shadow-[0px_4px_12px_0px_rgba(0,0,0,0.08)] gap-y-2 py-2">
                            {MINUTES.map((m) => (
                                <div
                                    key={m}
                                    className={`px-[16px] mx-3 py-[8px] cursor-pointer rounded-[7px] h-[33px] ${
                                        m === selectedMinute ? 'bg-[#EBF4FF]' : 'hover:bg-[#F6F8FA]'
                                    }`}
                                    onClick={() => {
                                        setSelectedMinute(m);
                                        setOpenPanel('none');
                                    }}
                                >
                                    <RegularText
                                        fontSize={15}
                                        lineHeight={22}
                                        color={m === selectedMinute ? '#3182F6' : '#191F28'}
                                    >
                                        {String(m).padStart(2, '0')}분
                                    </RegularText>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Row>

            {/* Action buttons */}
            <Row className="flex justify-end gap-[7px]">
                <button
                    className=" w-[116px] h-[40px] rounded-[11px] flex items-center justify-center bg-[#F2F4F6] hover:bg-[#E8EAED] transition-colors"
                    onClick={onClose}
                >
                    <RegularText fontSize={15} lineHeight={22} color="#4E5968">
                        취소
                    </RegularText>
                </button>
                <button
                    className="w-[116px] h-[40px] rounded-[11px] flex items-center justify-center bg-primary hover:bg-blue-600 transition-colors"
                    onClick={handleConfirm}
                >
                    <RegularText fontSize={15} lineHeight={22} color="white">
                        확인
                    </RegularText>
                </button>
            </Row>
        </div>
    );
};

export default SchedulePicker;
